import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Shield,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Moon,
  Sun,
  Cpu,
  Layers,
  Activity,
} from "lucide-react";
import { User } from "../../types";
import { CompanyLogo } from "../ui/company-logo";
import { apiService } from "../../utils/apiService";
import { mapUserDataFromBackend } from "../../utils/userDataMapper";

interface LoginFormProps {
  onLogin: (user: User) => void;
  onShowForgotPassword: () => void;
}

const THEME_KEY = "theme-override-guest";

export function LoginForm({
  onLogin,
  onShowForgotPassword,
}: LoginFormProps) {
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_KEY) || localStorage.getItem("theme");
    if (savedTheme === "dark") {
      setIsDarkMode(true);
      document.documentElement.classList.add("dark");
    } else if (savedTheme === "light") {
      setIsDarkMode(false);
      document.documentElement.classList.remove("dark");
    } else {
      const hasDark = document.documentElement.classList.contains("dark");
      setIsDarkMode(hasDark);
    }
  }, []);

  const toggleDarkMode = () => {
    const nextDark = !isDarkMode;
    setIsDarkMode(nextDark);
    const themeVal = nextDark ? "dark" : "light";
    localStorage.setItem(THEME_KEY, themeVal);
    localStorage.setItem("theme", themeVal);

    if (nextDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await apiService.login(
        formData.identifier,
        formData.password
      );

      if (response.data?.user) {
        const userData = mapUserDataFromBackend(response.data.user);
        onLogin(userData);
        return;
      }

      throw new Error("No user data returned from API");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Login failed"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-3.5 sm:p-6 lg:p-8 bg-slate-100 dark:bg-[#08090B] text-foreground relative overflow-y-auto font-sans select-none transition-colors duration-300">
      {/* Precision grid overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#64748b14_1px,transparent_1px),linear-gradient(to_bottom,#64748b14_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#1f293d0f_1px,transparent_1px),linear-gradient(to_bottom,#1f293d0f_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />

      {/* Theme toggle button */}
      <button
        onClick={toggleDarkMode}
        className="absolute top-3.5 right-3.5 sm:top-5 sm:right-5 z-30 p-2.5 rounded-lg bg-white dark:bg-[#121418] border border-slate-300 dark:border-[#232730] text-slate-700 dark:text-zinc-300 hover:text-slate-950 dark:hover:text-white hover:border-slate-400 dark:hover:border-zinc-500 hover:bg-slate-50 dark:hover:bg-[#1A1D24] transition-all shadow-sm cursor-pointer"
        aria-label="Toggle dark mode"
      >
        {isDarkMode ? <Sun className="h-4.5 w-4.5 sm:h-5 sm:w-5" /> : <Moon className="h-4.5 w-4.5 sm:h-5 sm:w-5" />}
      </button>

      {/* Main card container */}
      <div className="relative z-10 w-full max-w-4xl min-h-[480px] lg:min-h-[540px] bg-white dark:bg-[#121418] border border-slate-200 dark:border-[#232730] rounded-2xl shadow-xl dark:shadow-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2 transition-all my-auto">
        {/* Left Hero Panel */}
        <div
          className="relative hidden lg:flex flex-col justify-between p-8 sm:p-10 bg-cover bg-center overflow-hidden border-r border-slate-200 dark:border-[#232730]"
          style={{
            backgroundImage: "url('/rio_login_bg.png')",
          }}
        >
          {/* Contrast gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#08090B] via-[#08090B]/65 to-transparent pointer-events-none" />
          <div className="absolute inset-0 bg-black/25 pointer-events-none" />

          {/* Top Logo */}
          <div className="relative z-10">
            <CompanyLogo size="xl" showText={false} variant="dark" />
          </div>

          {/* Bottom Branding Content */}
          <div className="relative z-10 space-y-3.5">
            <div className="space-y-1">
              <h2 className="text-xl font-extrabold text-white tracking-tight font-display">
                PROJECT MANAGEMENT SYSTEM
              </h2>
            </div>

            <p className="text-xs text-zinc-300 leading-relaxed max-w-xs">
              Industrial project orchestration, precision fabrication tracking, and engineering lifecycle intelligence.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-2 pt-1.5">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#121418]/85 border border-[#232730] text-[11px] font-semibold text-zinc-200">
                <Cpu className="h-3 w-3 text-cyan-400" />
                Robotics R&D
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#121418]/85 border border-[#232730] text-[11px] font-semibold text-zinc-200">
                <Layers className="h-3 w-3 text-blue-400" />
                Fabrication
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#121418]/85 border border-[#232730] text-[11px] font-semibold text-zinc-200">
                <Activity className="h-3 w-3 text-emerald-400" />
                IoT & Systems Integration
              </div>
            </div>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="flex flex-col justify-center p-5 sm:p-8 lg:p-10 bg-white dark:bg-[#121418] transition-colors">
          {/* Mobile Logo Display */}
          <div className="lg:hidden flex items-center justify-center mb-5 pt-1">
            <CompanyLogo size="md" showText={false} variant="auto" />
          </div>

          <div className="w-full">
            {/* Header */}
            <div className="space-y-1 pb-5 sm:pb-6 text-left">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white tracking-tight font-display">
                Welcome Back
              </h1>
              <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-zinc-400">
                Enter your credentials to access your workspace
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Identifier Input */}
              <div className="space-y-1.5">
                <Label
                  htmlFor="identifier"
                  className="text-sm font-semibold text-slate-800 dark:text-zinc-200"
                >
                  Employee ID, Secure ID, or Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-zinc-400" />
                  <Input
                    id="identifier"
                    type="text"
                    placeholder="e.g., EMP001 or user@rio.ph"
                    value={formData.identifier}
                    onChange={(e) =>
                      handleInputChange("identifier", e.target.value)
                    }
                    required
                    disabled={isLoading}
                    className="h-11 pl-10 bg-slate-50 dark:bg-[#0E1014] border border-slate-300 dark:border-[#232730] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-500 focus:border-slate-900 dark:focus:border-white focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-white/20 rounded-lg text-sm font-sans transition-all"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="password"
                    className="text-sm font-semibold text-slate-800 dark:text-zinc-200"
                  >
                    Password
                  </Label>
                  <button
                    type="button"
                    onClick={onShowForgotPassword}
                    className="text-xs font-semibold text-slate-600 dark:text-zinc-300 hover:text-slate-950 dark:hover:text-white underline-offset-4 hover:underline transition-colors cursor-pointer"
                    disabled={isLoading}
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-zinc-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    required
                    disabled={isLoading}
                    className="h-11 pl-10 pr-10 bg-slate-50 dark:bg-[#0E1014] border border-slate-300 dark:border-[#232730] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-500 focus:border-slate-900 dark:focus:border-white focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-white/20 rounded-lg text-sm font-sans transition-all"
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-0 h-full px-3.5 text-slate-400 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Error Banner */}
              {error && (
                <Alert variant="destructive" className="py-2.5 px-3 bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200">
                  <AlertDescription className="text-xs font-medium">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {/* Primary Submit Button */}
              <Button
                type="submit"
                className="w-full h-11 bg-slate-950 dark:bg-white text-white dark:text-[#08090B] hover:bg-slate-800 dark:hover:bg-zinc-200 font-bold text-sm rounded-lg transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer mt-4"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                    Signing In...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4" />
                    Sign In to Workspace
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
